import {HookClass} from "@dvrt/graphql-widgets";
import {MutableRefObject, useEffect, useRef, useState} from "react";
import {Member, NewMemberInfo} from "./types";
import {Capability, GroupType} from "@/api";
import {
    CallGroupAddNewMemberGroupLazyQueryHookResult,
    CallGroupAddNewMemberExtensionLazyQueryHookResult,
    useCallGroupAddNewMemberExtensionLazyQuery,
    CallGroupAddNewMemberGroupQuery,
    useCallGroupAddNewMemberGroupLazyQuery,
} from "./queries.generated";

const initialData: Member[] = [];

// Store data for capabilities of each number id to check supervisor permission
export interface CapabilityData {
    numberId: number;
    capabilities: Capability[];
}

function sortMembers(members: Member[]) {
    return members.sort((a, b) => a.callOrder - b.callOrder);
}

export class DataSource extends HookClass {
    public groupId!: number;
    public domainId!: MutableRefObject<number | undefined>;
    public data!: MutableRefObject<Member[]>;

    public loaded!: boolean;
    private setLoaded!: (value: boolean) => void;

    private initialized!: MutableRefObject<boolean>;
    private group!: MutableRefObject<CallGroupAddNewMemberGroupQuery["groups"]["items"][number]>;
    private addedMembers!: MutableRefObject<Record<number, {shortNumber: string; displayName: string}>>;
    private capabilityDatas!: CapabilityData[];

    private groupQuery!: CallGroupAddNewMemberGroupLazyQueryHookResult[0];
    private extensionInfoQuery!: CallGroupAddNewMemberExtensionLazyQueryHookResult[0];

    useHooks(groupId: number) {
        [this.loaded, this.setLoaded] = useState<boolean>(false);
        this.groupId = groupId;
        this.domainId = useRef(undefined);
        this.data = useRef(initialData);
        this.initialized = useRef(false);
        this.group = useRef(undefined);
        this.addedMembers = useRef({});

        [this.groupQuery] = useCallGroupAddNewMemberGroupLazyQuery({
            variables: {
                groupId: this.groupId,
            },
            fetchPolicy: "no-cache",
        });
        [this.extensionInfoQuery] = useCallGroupAddNewMemberExtensionLazyQuery({
            fetchPolicy: "no-cache",
        });

        useEffect(() => {
            this.fetch();
            return () => {
                this.data.current = initialData;
            };
        }, []);
    }

    setNewMemberInfo(info: NewMemberInfo) {
        this.addedMembers.current[info.numberId] = {shortNumber: info.shortNumber, displayName: info.displayName};
    }

    get isCallQueue() {
        return this.group.current?.groupType === GroupType.CallQueue;
    }

    get groupInfo() {
        return this.group.current;
    }

    getCapabilityDatas() {
        return this.capabilityDatas;
    }

    get isInitialized() {
        return this.initialized.current;
    }

    get groupSize() {
        return this.group.current?.size;
    }

    getMemberInfo(numberId: number): Member {
        const memberInfo = this.data.current.filter((m) => m.numberId === numberId)[0];
        if (memberInfo) return memberInfo;
        const addedMemberInfo = this.addedMembers.current[numberId];
        if (addedMemberInfo)
            return {
                numberId,
                number: {
                    shortNumber: addedMemberInfo.shortNumber,
                    extension: {
                        displayName: addedMemberInfo.displayName,
                        alternateCallerNumberId: null,
                    },
                },
                callOrder: 0,
                sectionIndex: 0,
                controllingLine: false,
                allowEditAddressBook: false,
            };
        throw new Error(`Member with numberId ${numberId} not found`);
    }

    async fetch() {
        this.setLoaded(false);
        const {data: groupData} = await this.groupQuery();

        if (groupData.groups.items.length === 0) {
            return; // if no group found, they have no permissions
        }

        this.group.current = groupData.groups.items[0];

        if (this.domainId.current === undefined) {
            this.domainId.current = this.group.current.number.domainId;
        }

        const {data: extensionInfoData} = await this.extensionInfoQuery({
            variables: {
                domainId: this.domainId.current,
            },
            fetchPolicy: "no-cache",
        });

        this.data.current = sortMembers(this.group.current.members);

        type ExtensionCapabilities = {
            [extId: number]: {
                capabilities: Capability[];
                numberId: number;
            };
        };

        const extensionCapabilities: ExtensionCapabilities = {};

        extensionInfoData.users.items.forEach((ext) => {
            extensionCapabilities[ext.id] = {
                capabilities: [],
                numberId: ext.numberId,
            };
        });

        extensionInfoData.usersCapabilities.forEach((cap) => {
            extensionCapabilities[cap.id].capabilities = cap.capabilities;
        });

        const capDatas = Object.entries(extensionCapabilities).map(([_, data]) => {
            return {
                numberId: data.numberId,
                capabilities: data.capabilities,
            } as CapabilityData;
        });

        this.capabilityDatas = capDatas;

        this.initialized.current = true;
        this.setLoaded(true);
    }
}
